import { useEffect, useRef, useState } from 'react';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { PostPreviewHeader } from './PostPreviewHeader';
import { PreviewSection } from './PreviewSection';
import { EditSection } from './EditSection';
import { exportPostPreviewAsPNG } from '../../content-calendar/utils/export-post-preview';
import { uploadFileToServer } from '../../utils/server-upload';
import type { ActiveTab, Attachment, PostData, PreviewVariant } from './types';
import type { PostPreviewContent } from '@kontentino/kontentino-post-previews';
import type { Platform, PostStatus } from '../../content-calendar/types';

export interface PostPreviewLayoutProps {
  // Data
  post: PostData;
  onPostUpdate: (updates: Partial<PostData>) => void;

  // Preview content (transformed for PostPreview library)
  transformedContent: PostPreviewContent;

  // Display mode (pip is treated as inline)
  displayMode: 'inline' | 'fullscreen' | 'pip';

  // Navigation (optional)
  showNavigation?: boolean;
  onNavigatePrev?: () => void;
  onNavigateNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;

  // Actions
  onSchedule?: (post: PostData) => void;
  onClose?: () => void;
  onExportComplete?: (uploadedUrl: string, filename: string) => void;
}

export function PostPreviewLayout({
                                    post,
                                    onPostUpdate,
                                    transformedContent,
                                    displayMode,
                                    showNavigation = false,
                                    onNavigatePrev,
                                    onNavigateNext,
                                    hasPrev = false,
                                    hasNext = false,
                                    onSchedule,
                                    onClose,
                                    onExportComplete,
                                  }: PostPreviewLayoutProps) {
  const [previewVariant, setPreviewVariant] = useState<PreviewVariant>('desktop');
  const [activeTab, setActiveTab] = useState<ActiveTab>('preview');
  const [isExporting, setIsExporting] = useState(false);
  const [isEditingTopic, setIsEditingTopic] = useState(false);
  const [editedTopic, setEditedTopic] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const topicInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  // Treat 'pip' as 'inline' for layout purposes
  const isInline = displayMode === 'inline' || displayMode === 'pip';
  const isScheduled = post.status === 'scheduled';

  // Sync local edit state when post changes
  useEffect(() => {
    setEditedTopic(post.topic || '');
  }, [post.topic]);

  // Auto-focus when entering edit mode
  useEffect(() => {
    if (isEditingTopic && topicInputRef.current) {
      topicInputRef.current.focus();
      topicInputRef.current.select();
    }
  }, [isEditingTopic]);

  const handleExportPNG = async () => {
    if (!previewContainerRef.current) return;

    setIsExporting(true);
    try {
      // Create a temporary post object with required fields for export
      const postForExport = {
        ...post,
        platform: (post.platform || 'instagram') as Platform,
        date: post.date || new Date().toISOString().split('T')[0],
        topic: post.topic || 'Post Preview',
      };

      const exportData = await exportPostPreviewAsPNG(previewContainerRef.current, postForExport as any);

      // Upload to server
      const uploadResult = await uploadFileToServer({
        dataUrl: exportData.dataUrl,
        mimeType: 'image/png',
        filename: exportData.filename
      });

      // Notify parent component to show export dialog
      if (onExportComplete) {
        onExportComplete(uploadResult.url, exportData.filename);
      }

      console.log('[Export] PNG exported successfully:', exportData.filename);
    } catch (error) {
      console.error('[Export] Failed to export PNG:', error);
      alert('Failed to export preview. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleSchedule = () => {
    if (onSchedule) {
      onSchedule(post);
    }
  };

  const handleTopicBlur = () => {
    if (editedTopic.trim() && editedTopic.trim() !== post.topic) {
      onPostUpdate({topic: editedTopic.trim()});
    }
    setIsEditingTopic(false);
  };

  const handleTextChange = (text: string) => {
    onPostUpdate({text});
  };

  const handleAttachmentsChange = (attachments: Attachment[]) => {
    onPostUpdate({attachments});
  };

  const handleDateChange = (date: string) => {
    onPostUpdate({date});
  };

  const handleTimeChange = (time: string) => {
    onPostUpdate({time});
  };

  const handlePlatformChange = (platform: Platform) => {
    onPostUpdate({platform});
  };

  const handleStatusChange = (status: PostStatus) => {
    onPostUpdate({status});
  };

  return (
    <div
      className={`w-full flex flex-col p-0 gap-0 bg-background border rounded-lg`}>
      {/* Header */}
      <PostPreviewHeader
        isInline={isInline}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        topic={post.topic}
        date={post.date}
        time={post.time}
        isEditingTopic={isEditingTopic}
        editedTopic={editedTopic}
        onTopicChange={setEditedTopic}
        onTopicBlur={handleTopicBlur}
        onTopicEditStart={() => setIsEditingTopic(true)}
        topicInputRef={topicInputRef}
        showNavigation={showNavigation}
        onNavigatePrev={onNavigatePrev}
        onNavigateNext={onNavigateNext}
        hasPrev={hasPrev}
        hasNext={hasNext}
        isExporting={isExporting}
        onExportPNG={handleExportPNG}
        onSchedule={onSchedule ? handleSchedule : undefined}
        isScheduled={isScheduled}
        onClose={onClose}
      />

      {/* Content: Inline mode - tabs on mobile, side-by-side on larger screens */}
      {isInline && (
        <>
          {/* Tabs layout for < 700px */}
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as ActiveTab)}
                className="flex flex-col flex-1 min-h-0 [@media(min-width:700px)]:hidden">
            {/* Preview Tab */}
            <TabsContent value="preview" className="flex flex-col flex-1 min-h-0 mt-0 px-3 bg-muted/60">
              <PreviewSection
                content={transformedContent}
                previewVariant={previewVariant}
                onPreviewVariantChange={setPreviewVariant}
                previewContainerRef={previewContainerRef}
                isInline={true}
              />
            </TabsContent>

            {/* Edit Tab */}
            <TabsContent value="edit" className="flex flex-col flex-1 min-h-0 mt-0 px-3 bg-muted/60">
              <EditSection
                text={post.text}
                onTextChange={handleTextChange}
                attachments={post.attachments || []}
                onAttachmentsChange={handleAttachmentsChange}
                date={post.date || new Date().toISOString().split('T')[0]}
                time={post.time || '12:00'}
                platform={(post.platform || 'instagram') as Platform}
                status={(post.status || 'draft') as PostStatus}
                pageName={post.pageName}
                onDateChange={handleDateChange}
                onTimeChange={handleTimeChange}
                onPlatformChange={handlePlatformChange}
                onStatusChange={handleStatusChange}
                dropZoneRef={dropZoneRef}
                isUploading={isUploading}
                setIsUploading={setIsUploading}
                uploadError={uploadError}
                setUploadError={setUploadError}
                isInline={true}
              />
            </TabsContent>
          </Tabs>

          {/* Side-by-side layout for >= 700px */}
          <div className="hidden [@media(min-width:700px)]:grid grid-cols-2 gap-3 flex-1 min-h-0 px-2 bg-muted/60">            {/* Left column: Preview */}
            <PreviewSection
              content={transformedContent}
              previewVariant={previewVariant}
              onPreviewVariantChange={setPreviewVariant}
              previewContainerRef={previewContainerRef}
              isInline={true}
            />

            {/* Right column: Editing */}
            <EditSection
              text={post.text}
              onTextChange={handleTextChange}
              attachments={post.attachments || []}
              onAttachmentsChange={handleAttachmentsChange}
              date={post.date || new Date().toISOString().split('T')[0]}
              time={post.time || '12:00'}
              platform={(post.platform || 'instagram') as Platform}
              status={(post.status || 'draft') as PostStatus}
              pageName={post.pageName}
              onDateChange={handleDateChange}
              onTimeChange={handleTimeChange}
              onPlatformChange={handlePlatformChange}
              onStatusChange={handleStatusChange}
              dropZoneRef={dropZoneRef}
              isUploading={isUploading}
              setIsUploading={setIsUploading}
              uploadError={uploadError}
              setUploadError={setUploadError}
              isInline={true}
            />
          </div>
        </>
      )}

      {/* Content: Fullscreen mode - side by side */}
      {!isInline && (
        <div
          className="grid grid-cols-1 [@media(min-width:700px)]:grid-cols-2 gap-4 [@media(min-width:700px)]:gap-6 flex-1 min-h-0 px-4 [@media(min-width:700px)]:px-6 bg-muted/60">
          {/* Left column: Preview */}
          <PreviewSection
            content={transformedContent}
            previewVariant={previewVariant}
            onPreviewVariantChange={setPreviewVariant}
            previewContainerRef={previewContainerRef}
          />

          {/* Right column: Editing */}
          <EditSection
            text={post.text}
            onTextChange={handleTextChange}
            attachments={post.attachments || []}
            onAttachmentsChange={handleAttachmentsChange}
            date={post.date || new Date().toISOString().split('T')[0]}
            time={post.time || '12:00'}
            platform={(post.platform || 'instagram') as Platform}
            status={(post.status || 'draft') as PostStatus}
            pageName={post.pageName}
            onDateChange={handleDateChange}
            onTimeChange={handleTimeChange}
            onPlatformChange={handlePlatformChange}
            onStatusChange={handleStatusChange}
            dropZoneRef={dropZoneRef}
            isUploading={isUploading}
            setIsUploading={setIsUploading}
            uploadError={uploadError}
            setUploadError={setUploadError}
          />
        </div>
      )}
    </div>
  );
}
